<template>
  <div id="monthly-labor-record" ref="crewLaborRecord">
    <div class="crew-labor-record-table h-full">
      <vs-row
        id="monthly-labor-record-header"
        vs-w="12"
        class="mt-4 gap-4"
        ref="crewLaborRecordHeader"
      >
        <!-- ダウンロードボタン -->
        <div class="flex flex-start items-center w-full">
          <div class="flex flex-grow">
            <div
              class="vessel-name flex flex-start items-center aisea-text-16-700-black mr-12"
            >
              {{ vesselName }}
            </div>
            <treeselect
              v-model="selectedYear"
              :options="yearOptions"
              :multiple="false"
              :clearable="false"
              placeholder="年"
              noOptionsText="なし"
              class="mr-4 my-1 w-32"
              @input="refetchData"
              noResultsText="見つかりませんでした"
            ></treeselect>
            <treeselect
              v-model="selectedMonth"
              :options="monthOptions"
              :multiple="false"
              :clearable="false"
              placeholder="月"
              noOptionsText="なし"
              class="mr-4 my-1 w-24"
              @input="refetchData"
              noResultsText="見つかりませんでした"
            ></treeselect>
            <treeselect
              v-model="selectedMode"
              :options="modeOptions"
              valueFormat="object"
              :multiple="false"
              :clearable="false"
              placeholder="労働時間/1日"
              noOptionsText="なし"
              class="mr-4 my-1 w-64"
              noResultsText="見つかりませんでした"
            ></treeselect>
          </div>
          <button
            type="button"
            class="vs-button includeIcon download-btn"
            @click.stop.prevent="() => handleClickDownload()"
            :disabled="isRecordBookNotFound"
          >
            <feather-icon
              icon="DownloadIcon"
              svgClasses="h-4 w-4"
              class="mr-2"
            />
            <span class="vs-button-text vs-button--text"> ダウンロード </span>
          </button>
        </div>
      </vs-row>

      <!-- 作業グラフなどのテーブル -->
      <ag-grid-vue
        v-if="!isRecordBookNotFound"
        class="ag-theme-material w-100 my-6 ag-grid-table flex-shrink-1"
        ref="agGridTableCrewStatusList"
        :gridOptions="gridOptions"
        domLayout="autoHeight"
        :defaultColDef="defaultColDef"
        :columnDefs="columnDef"
        :rowData="rowData"
        colResizeDefault="shift"
        :suppressCellFocus="true"
        :animateRows="false"
        :floatingFilter="false"
      >
      </ag-grid-vue>

      <div class="flex flex-col items-center justify-center h-full" v-else>
        <div class="aisea-text-16-400-gray my-20">
          連携対象期間外のデータは表示されません
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import moment from "moment-timezone";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import { DeviceInfoMixin, ColorMixin } from "@/mixins";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import CellRendererHeaderRole from "../components/CellRendererHeaderRole.vue";
const enterpriseOwnerManningCrewApi = AiseaApiFactory.get(
  "enterpriseOwnerManningCrew"
);
const enterpriseOwnerManningVesselRecordBookApi = AiseaApiFactory.get(
  "enterpriseOwnerManningVesselRecordBook"
);
const enterpriseOwnerManningVesselApi = AiseaApiFactory.get(
  "enterpriseOwnerManningVessel"
);
import FileSaver from "file-saver";
const MONTHS = [
  "1月",
  "2月",
  "3月",
  "4月",
  "5月",
  "6月",
  "7月",
  "8月",
  "9月",
  "10月",
  "11月",
  "12月",
];
const WEEKDAYS_SHORT = ["日", "月", "火", "水", "木", "金", "土"];

export default {
  name: "MonthlyLaborRecord",
  components: {
    AgGridVue,
    Treeselect,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererHeaderRole,
  },
  props: {
    enterpriseVesselId: { type: String, require: true },
    active: { type: Boolean, default: false },
    cellSize: { type: Number, default: 30 },
    cellNum: { type: Number, default: 24 },
    activeTab: { type: Number },
  },
  mixins: [DeviceInfoMixin, ColorMixin],
  data() {
    return {
      vesselName: "",
      yearOptions: this.generateYearOptions(2024),
      monthOptions: MONTHS.map((month, index) => {
        return {
          id: index + 1,
          label: month,
        };
      }),
      components: {
        CellRendererHeaderRole,
      },
      modeOptions: [
        {
          id: "daily_working_time",
          label: "1日当たりの労働時間",
        },
        {
          id: "max_of_divided_rest_time",
          label: "連続休息時間",
          critical: "is_hour_of_rest24h_critical",
        },
        {
          id: "5days_working_time",
          label: "過去5日間以内の労働時間",
          warning: "is_hour_of_work5d_warning",
        },
        {
          id: "7days_working_time",
          label: "過去7日間以内の労働時間",
          warning: "is_hour_of_work1w_warning",
          critical: "is_hour_of_work1w_critical",
        },
        {
          id: "28days_overtime",
          label: "過去28日間の時間外労働",
          critical: "is_hour_of_overtime_work28d_critical",
        },
      ],

      selectedMode: null,
      selectedMonth: null,
      selectedYear: null,

      anonymusCrews: [],
      recordBooks: [],

      columnDefs: null,

      gridOptions: {
        localeText: { noRowsToShow: "表示する運航船月次詳細情報がありません" },
        context: {
          componentParent: this,
        },
      },

      defaultColDef: {
        resizable: true,
        sortable: false,
        wrapText: true,
        autoHeight: false,
      },

      isRecordBookNotFound: false,
    };
  },

  computed: {
    startAndEndOfMonthDates() {
      const year = this.selectedYear;
      const month = this.selectedMonth;
      if (!year || !month) return {};
      // 月初の日付
      const startOfMonth = moment({
        year: year,
        month: month - 1,
        day: 1,
      }).startOf("month");
      // 月末の日付
      const endOfMonth = moment({ year: year, month: month - 1, day: 1 }).endOf(
        "month"
      );

      return {
        startOfMonth: startOfMonth,
        endOfMonth: endOfMonth,
      };
    },

    columnDef() {
      return [
        {
          headerName: "日付",
          field: "date",
          width: 120,
          pinned: "left",
        },
        ...this.anonymusCrews.map(crew => {
          return {
            headerName: crew.id,
            headerComponentParams: { roles: crew.roles, id: crew.id },
            field: crew.id.toString(),
            valueFormatter: params =>
              params.value ? params.value.value : null,
            cellClassRules: {
              "cell-danger": params =>
                params.value ? params.value.critical : false,
              "cell-warn": params =>
                params.value ? params.value.warning : false,
            },
            headerComponentFramework: "CellRendererHeaderRole",
            width: 100,
          };
        }),
      ];
    },
    rowData() {
      if (this.anonymusCrews.length === 0) return [];

      const targetMonthMomentArray = this.getStartAndEndOfMonth(
        this.selectedYear,
        this.selectedMonth
      );

      return targetMonthMomentArray.map(date => {
        return {
          date: date.local().format("M/D(ddd)"),
          ...this.anonymusCrews.reduce((acc, crew) => {
            const targetRecordBook = this.recordBooks.find(
              recordBook => recordBook.enterprise_crew_id === crew.id
            );
            const targetInlineRecordBook = targetRecordBook
              ? targetRecordBook[
                  "inline_response_enterprise_crew_record_books"
                ].find(recordBook =>
                  moment(recordBook.date).isSame(date, "day")
                )
              : null;
            const value = targetInlineRecordBook
              ? targetInlineRecordBook[this.selectedMode.id] || 0
              : 0;
            acc[crew.id] = {};
            acc[crew.id]["value"] = this.getTimeFromMinutes(value);

            acc[crew.id]["warning"] = targetInlineRecordBook
              ? targetInlineRecordBook[this.selectedMode.warning]
              : false;
            acc[crew.id]["critical"] = targetInlineRecordBook
              ? targetInlineRecordBook[this.selectedMode.critical]
              : false;

            return acc;
          }, {}),
        };
      });
    },
  },
  methods: {
    refetchData() {
      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      const enterpriseManningVesselId = this.$route.params.id;
      Promise.all([
        this.fetchAnonymusCrews(),
        this.fetchRecordBooks(enterpriseManningVesselId),
      ])
        .catch(() => {
          this.$vs.notify({
            title: "データの取得に失敗しました",
            color: "danger",
            iconPack: "feather",
            icon: "icon-x-square",
          });
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    },
    generateYearOptions(startYear) {
      const currentYear = moment().year();
      const yearArray = [];

      for (let year = startYear; year <= currentYear; year++) {
        yearArray.push({
          id: year,
          label: `${year}年`,
        });
      }

      return yearArray;
    },
    getTimeFromMinutes(minutes) {
      if (typeof minutes !== "number") return null;
      const hours = String(Math.floor(minutes / 60)).padStart(2, 0);
      const restMinuites = String(minutes % 60).padStart(2, 0);
      return `${hours}:${restMinuites}`;
    },
    getStartAndEndOfMonth(year, month) {
      const startOfMonth = moment({ year: year, month: month - 1 }).startOf(
        "month"
      );
      const endOfMonth = moment({ year: year, month: month - 1 }).endOf(
        "month"
      );

      let dates = [];
      let current = startOfMonth
        .locale("ja", {
          weekdaysShort: WEEKDAYS_SHORT,
        })
        .clone();

      while (current.isSameOrBefore(endOfMonth)) {
        dates.push(current.clone());
        current.add(1, "days");
      }

      return dates;
    },

    async fetchAnonymusCrews() {
      const enterpriseManningVesselId = this.$route.params.id;
      const res = await enterpriseOwnerManningCrewApi.fetchCrews({
        from_date: moment(
          `${this.selectedYear}/${this.selectedMonth}/1`
        ).format("YYYY-MM-DD"),
        to_date: moment(`${this.selectedYear}/${this.selectedMonth}/1`)
          .add(1, "month")
          .add(-1, "day")
          .format("YYYY-MM-DD"),
        vessel_ids: [enterpriseManningVesselId].toString(),
        size: 1000,
      });
      if (res.status) {
        this.anonymusCrews = res.data.enterprise_crews.map(crew => {
          return {
            id: crew.id,
            roles: crew.enterprise_manning_vessel_crew
              .map(vesselCrew =>
                vesselCrew.roles
                  .map(role => {
                    // 選択された年月にかかっている役職のみを抽出し、開始日と終了日を選択された年月に収まるように調整
                    const roleStart = moment(role.start_on);
                    const roleEnd = moment(role.end_on);
                    const targetStart = moment([
                      this.selectedYear,
                      this.selectedMonth - 1,
                    ]); // 対象年月の1日
                    const targetEnd = targetStart.clone().endOf("month"); // 対象年月の末日

                    // 選択された年月にかぶっているかどうかをチェック
                    if (
                      roleStart.isSameOrBefore(targetEnd) &&
                      roleEnd.isSameOrAfter(targetStart)
                    ) {
                      // 開始日と終了日の調整
                      const adjustedStartOn = roleStart.isBefore(targetStart)
                        ? targetStart
                        : roleStart;
                      const adjustedEndOn = roleEnd.isAfter(targetEnd)
                        ? targetEnd
                        : roleEnd;

                      return {
                        startOn: adjustedStartOn.format("YYYY-MM-DD"),
                        endOn: adjustedEndOn.format("YYYY-MM-DD"),
                        name: role.localized_key === "other" && role.name
                          ? role.name
                          : this.$t(`Views.Crew.${role.localized_key}`),
                      };
                    }
                    return null;
                  })
                  .filter(role => role !== null)
              )
              .flat(2),
          };
        });
      } else {
        throw Error();
      }
    },
    async fetchRecordBooks(enterpriseManningVesselId) {
      const { startOfMonth, endOfMonth } = this.startAndEndOfMonthDates;
      const res = await enterpriseOwnerManningVesselRecordBookApi.fetchRecordBooks(
        enterpriseManningVesselId,
        {
          start_at: startOfMonth.format("YYYY-MM-DD"),
          end_at: endOfMonth.format("YYYY-MM-DD"),
          need_timesheet: false,
        }
      );
      if (res.status) {
        this.recordBooks = res.data;
        this.isRecordBookNotFound = false;
      } else if (res.code === 404) {
        this.isRecordBookNotFound = true;
      } else {
        throw Error();
      }
    },
    async fetchCurrentVesselInfo(enterpriseManningVesselId) {
      const res = await enterpriseOwnerManningVesselApi.fetchVesselInfo(
        enterpriseManningVesselId
      );
      if (res.status) {
        this.vesselName = res.data.name;
      } else {
        throw Error();
      }
    },
    async handleClickDownload() {
      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      const enterpriseManningVesselId = this.$route.params.id;
      const { startOfMonth, endOfMonth } = this.startAndEndOfMonthDates;
      try {
        const res = await enterpriseOwnerManningVesselRecordBookApi.downloadMonthlyRecordBook(
          enterpriseManningVesselId,
          {
            start_at: startOfMonth.format("YYYY-MM-DD"),
            end_at: endOfMonth.format("YYYY-MM-DD"),
          }
        );
        if (res.status !== true) throw new Error();
        const fileName = `${
          this.vesselName
        }_運航船労務状況一覧（月次）${startOfMonth.format("YYYYMM")}.xlsx`;
        FileSaver.saveAs(res.data, fileName);
        this.$vs.loading.close();
      } catch (e) {
        this.$vs.loading.close();
        this.$vs.notify({
          title: "ダウンロードに失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      }
    },
  },

  async created() {
    this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
    this.selectedMode = this.modeOptions.find(
      mode => mode.id === "daily_working_time"
    );
    this.selectedMonth = this.monthOptions.find(
      month => month.id === moment().month() + 1
    ).id;
    this.selectedYear = this.yearOptions.find(
      year => year.id === moment().year()
    ).id;
    const enterpriseManningVesselId = this.$route.params.id;
    Promise.all([
      this.fetchAnonymusCrews(),
      this.fetchRecordBooks(enterpriseManningVesselId),
      this.fetchCurrentVesselInfo(enterpriseManningVesselId),
    ])
      .catch(() => {
        this.$vs.notify({
          title: "データの取得に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      })
      .finally(() => {
        this.$vs.loading.close();
      });
  },

  async mounted() {
    let routerViewElement = document.getElementsByClassName("router-view");
    routerViewElement[0].style.padding = "2.2rem";
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

#monthly-labor-record {
  --vs-primary: 3, 155, 229;
  height: 100%;
  .vx-card {
    padding: 20px;
  }
  #monthly-labor-record-header {
    .download-btn {
      border: 1px solid rgb(var(--vs-primary));
      background: transparent;
      color: rgb(var(--vs-primary));
      height: 2.7rem;
    }
  }

  .cell-warn {
    color: $aisea-white;
    background-color: #ffbf00;
    font-weight: 600;
  }

  .cell-danger {
    color: $aisea-white;
    background-color: #eb5f56;
    font-weight: 600;
  }

  .ag-header-cell {
    padding: 0px 18px;
  }

  .ag-cell-value {
    @extend .ag-header-cell;
  }

  .ag-grid-table {
    height: 100%;
  }
}
</style>
